import type { AiSuggestionInteractionPoint } from '@atlaskit/editor-common/analytics/ai-suggestions-regeneration';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { getAnnotationIdsFromRange } from '@atlaskit/editor-common/utils/annotation';
import { Selection } from '@atlaskit/editor-prosemirror/state';

import type { AiSuggestionsPlugin, AiSuggestionsPluginOptions } from '../aiSuggestionsPluginType';
import type { AcceptSuggestionExperience } from '../experiences/accept-suggestion-experience';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';
import { getSuggestionChangeStart } from '../pm-plugins/utils/suggestionToolCallRange';
import { triggerSuggestedRemix } from '../ui/components/SuggestionCard/triggerSuggestedRemix';
import { addAcceptSuggestionAnalytics } from '../ui/utils/analytics';
import { updateSuggestionStatus } from '../ui/utils/api';
import { applyToolCalls } from '../ui/utils/toolCalls/applyToolCalls';
import type { AcceptSuggestionAction } from './types';

export const acceptSuggestionAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
	onInlineCommentSuggestionAccepted?: AiSuggestionsPluginOptions['onInlineCommentSuggestionAccepted'],
	acceptSuggestionExperience?: AcceptSuggestionExperience,
) => AcceptSuggestionAction =
	(api, onInlineCommentSuggestionAccepted, acceptSuggestionExperience) =>
	(
		id: string,
		interactionPoint: AiSuggestionInteractionPoint = 'sidebar',
		isDiffHidden: boolean = false,
	): boolean => {
		const experience = acceptSuggestionExperience?.start(interactionPoint);
		try {
			const suggestion = api?.aiSuggestions?.sharedState.currentState()?.suggestionById.get(id);
			if (!suggestion) {
				experience?.failure({ reason: 'suggestionNotFound' });
				return false;
			}

			const isRemix = suggestion.actionKind === 'SUGGESTED_REMIX';
			let removedAnnotationIds: string[] = [];

			api?.core?.actions.execute(({ tr }) => {
				addAcceptSuggestionAnalytics(api?.analytics?.actions)(
					tr,
					suggestion,
					interactionPoint,
					isDiffHidden,
				);
				tr.setMeta('addToHistory', false);
				// Move the selection to the change before the accept so history bookmarks it: undo then
				// restores and scrolls to it, instead of the pre-accept selection (often the doc start).
				const changeStart = isRemix ? undefined : getSuggestionChangeStart(tr.doc, suggestion);
				if (changeStart !== undefined && changeStart <= tr.doc.content.size) {
					tr.setSelection(Selection.near(tr.doc.resolve(changeStart)));
				}
				return tr;
			});

			const accepted = api?.core?.actions.execute(({ tr }) => {
				api?.showDiff?.commands.hideDiff?.({ tr });

				if (isRemix) {
					tr.setMeta(aiSuggestionsPluginKey, { action: 'REMOVE_SUGGESTION', id });
				} else {
					const annotationIdsBefore = getAnnotationIdsFromRange(
						{ from: 0, to: tr.doc.content.size },
						tr.doc,
						tr.doc.type.schema,
					);
					applyToolCalls({
						tr,
						suggestion,
						onToolCallNotApplied: () => experience?.failure({ reason: 'toolCallNotApplied' }),
					});
					const annotationIdsAfter = getAnnotationIdsFromRange(
						{ from: 0, to: tr.doc.content.size },
						tr.doc,
						tr.doc.type.schema,
					);
					removedAnnotationIds = annotationIdsBefore.filter(
						(annotationId) => !annotationIdsAfter.includes(annotationId),
					);
					tr.setMeta('scrollIntoView', false);
					tr.setMeta(aiSuggestionsPluginKey, { action: 'ACCEPT_SUGGESTION', suggestion });
				}

				return tr;
			});

			if (!accepted || acceptSuggestionExperience?.isAccepted(id) === false) {
				experience?.failure({ reason: 'acceptTransactionNotApplied' });
			}

			updateSuggestionStatus({ updates: [{ id, status: 'ACTIONED' }] }).then(
				() => experience?.success(),
				() => experience?.failure({ reason: 'updateStatusFailed' }),
			);

			if (removedAnnotationIds.length > 0) {
				onInlineCommentSuggestionAccepted?.(suggestion);
			}

			if (isRemix) {
				const action = suggestion.action;
				triggerSuggestedRemix({
					api,
					launchOptions: action?.launchOptions,
					localIds: action?.localIds,
					userPrompt: action?.userPrompt,
				});
			}

			return true;
		} catch (error) {
			experience?.failure({ reason: 'acceptSuggestionError' });
			throw error;
		}
	};
